/**
 * Who is signed in, and what they may do.
 *
 * Permissions come from the server and are used only to decide what to
 * *render*. Every one of them is checked again server-side — the frontend must
 * never make a permission decision the server does not also make
 * (docs/08-uiux.md §12).
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { api, ApiError, onSessionLost, onSessionRefreshed } from '@/lib/api'
import { connectSocket, disconnectSocket, getSocket, onConnectionChange } from '@/lib/socket'
import { refreshBranding } from '@/lib/branding'
import type { PropertyBrief, SessionInfo, UserBrief } from '@/lib/types'

/** A change the server has already accepted, to show without a round trip. */
export interface SessionPatch {
  user?: Partial<UserBrief>
  property?: Partial<PropertyBrief>
}

interface AuthContextValue {
  session: SessionInfo | null
  loading: boolean
  signIn: (username: string, password: string) => Promise<SessionInfo>
  signOut: () => Promise<void>
  refresh: () => Promise<void>
  can: (...permissions: string[]) => boolean
  canAny: (...permissions: string[]) => boolean
  /**
   * The properties a cross-property administrator may switch between. Empty for
   * everyone else — a property-scoped user is fixed to the property on their
   * account and cannot change it.
   */
  properties: PropertyBrief[]
  /** True once the property list has been fetched (or determined N/A). */
  propertiesLoaded: boolean
  /** True while a super admin's working property is being resolved. */
  resolvingProperty: boolean
  /** Why the working property could not be opened, if it could not. */
  propertyError: string | null
  /** Put this session into a property and refetch everything scoped to it. */
  selectProperty: (propertyId: string) => Promise<void>
  /**
   * Show a saved name, logo or picture at once. Not `refresh`: a super admin's
   * `/auth/me` carries no property (the account has none of its own), so a
   * refetch would drop the one they are working in.
   */
  patchSession: (patch: SessionPatch) => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<SessionInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [properties, setProperties] = useState<PropertyBrief[]>([])
  const [propertiesLoaded, setPropertiesLoaded] = useState(false)
  const [resolvingProperty, setResolvingProperty] = useState(false)
  const [propertyError, setPropertyError] = useState<string | null>(null)
  // Properties the automatic pick has already tried and failed on. Without
  // this the retry below is unbounded — see the effect that reads it.
  const autoSelectFailed = useRef<Set<string>>(new Set())
  const queryClient = useQueryClient()

  const load = useCallback(async () => {
    try {
      const info = await api.get<SessionInfo>('/auth/me')
      setSession(info)
      connectSocket()
    } catch (error) {
      // A 401 here is the normal "not signed in yet" case, not a failure.
      if (!(error instanceof ApiError) || error.status !== 401) {
        console.error('Could not load the session', error)
      }
      setSession(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const signIn = useCallback(async (username: string, password: string) => {
    const info = await api.post<SessionInfo>('/auth/login', { username, password })
    setSession(info)
    connectSocket()
    return info
  }, [])

  const signOut = useCallback(async () => {
    try {
      await api.post('/auth/logout')
    } finally {
      disconnectSocket()
      setSession(null)
      setProperties([])
      setPropertyError(null)
      autoSelectFailed.current.clear()
    }
  }, [])

  const selectProperty = useCallback(
    async (propertyId: string) => {
      setResolvingProperty(true)
      setPropertyError(null)
      try {
        const info = await api.post<SessionInfo>('/auth/select-property', {
          property_id: propertyId,
        })
        setSession(info)
        // Everything already fetched was scoped to the old (or no) property, so
        // it is all stale now. Refetch against the new scope.
        await queryClient.invalidateQueries()
        // The property scope lives in the access-token cookie, which just
        // changed; reconnect so the socket handshake carries the new scope.
        disconnectSocket()
        connectSocket()
        // It worked, so an earlier failure on this one is history.
        autoSelectFailed.current.delete(propertyId)
      } finally {
        setResolvingProperty(false)
      }
    },
    [queryClient],
  )

  // A cross-property administrator has no property of their own. Fetch the ones
  // they may work in, and — the common single-hotel case — pick it for them so
  // the app is usable without a first, mysterious "select a property" step.
  const isSuperAdmin = (session?.permissions ?? []).includes('system:admin')
  useEffect(() => {
    if (!session || !isSuperAdmin) {
      setProperties([])
      setPropertiesLoaded(!session ? false : true)
      return
    }
    let cancelled = false
    void (async () => {
      try {
        const list = await api.get<PropertyBrief[]>('/properties')
        if (!cancelled) setProperties(list)
      } catch (error) {
        console.error('Could not list properties', error)
      } finally {
        if (!cancelled) setPropertiesLoaded(true)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [session, isSuperAdmin])

  // The server keeps a super admin's property across a token refresh. Should a
  // refresh ever come back without it anyway - a session signed in before the
  // server kept it, say - put it back, rather than leave every scoped call,
  // the capture inbox included, answering 403 until somebody reloads.
  const sessionRef = useRef(session)
  sessionRef.current = session
  useEffect(
    () =>
      onSessionRefreshed((info) => {
        const working = sessionRef.current?.property
        if (
          !working ||
          info.property ||
          !info.permissions.includes('system:admin') ||
          autoSelectFailed.current.has(working.id)
        ) {
          return
        }
        void selectProperty(working.id).catch((error: Error) => {
          autoSelectFailed.current.add(working.id)
          setPropertyError(error.message || 'That property could not be opened.')
        })
      }),
    [selectProperty],
  )

  // What they may do, re-read without signing out. The server decides every
  // request from the database, so a permission an admin grants works at once;
  // this is only the menu catching up. Only roles and permissions are taken
  // from /auth/me — a super admin's carries no property, see `patchSession`.
  const syncAccess = useCallback(
    async (fresh?: SessionInfo) => {
      if (!sessionRef.current) return
      let info = fresh
      if (!info) {
        try {
          info = await api.get<SessionInfo>('/auth/me')
        } catch (error) {
          // Suspended or deleted: the API has already stopped serving them.
          if (error instanceof ApiError && error.status === 401) {
            sessionRef.current = null
            disconnectSocket()
            setSession(null)
          }
          return
        }
      }
      const before = sessionRef.current
      if (!before) return
      const same = (a: string[], b: string[]) =>
        a.length === b.length && [...a].sort().join() === [...b].sort().join()
      if (same(before.permissions, info.permissions) && same(before.roles, info.roles)) return
      const { roles, permissions } = info
      setSession((current) => (current ? { ...current, roles, permissions } : current))
      // A page that has just been refused ("Permission denied … Try again")
      // loads by itself, and one that is no longer allowed stops showing data.
      void queryClient.invalidateQueries()
    },
    [queryClient],
  )

  // The sign-in was ended elsewhere - "Sign out everywhere", a password
  // change, sign-out in another tab. Back to the sign-in page, not a screen of
  // errors that still looks signed in.
  useEffect(
    () =>
      onSessionLost(() => {
        if (!sessionRef.current) return
        sessionRef.current = null
        disconnectSocket()
        setSession(null)
        setProperties([])
        setPropertyError(null)
        autoSelectFailed.current.clear()
        queryClient.clear()
      }),
    [queryClient],
  )

  // When to re-read: an admin changed their access or a role they hold (the
  // server says so on the socket), the socket came back after missing that,
  // a token refresh brought a new list anyway, or the tab is looked at again
  // (the fallback for a network that will not hold a websocket).
  const signedIn = session !== null
  useEffect(() => {
    if (!signedIn) return
    let bound: ReturnType<typeof getSocket> = null
    const changed = () => void syncAccess()
    // The server closed the socket. It does that to a sign-in it has just
    // ended, and the close can overtake the `auth.revoked` sent before it (the
    // event goes round Redis, the close does not). Socket.IO will not reconnect
    // by itself after a server close, so reconnect only if still signed in.
    const kicked = (reason: string) => {
      if (reason !== 'io server disconnect') return
      void syncAccess().then(() => {
        if (sessionRef.current) connectSocket()
      })
    }
    const offConnection = onConnectionChange((state) => {
      if (state !== 'connected') return
      const current = getSocket()
      if (current && current !== bound) {
        bound?.off('access.changed', changed)
        bound?.off('auth.revoked', changed)
        bound?.off('disconnect', kicked)
        current.on('access.changed', changed)
        current.on('disconnect', kicked)
        // Signed out from elsewhere. Re-reading the session is what proves
        // it (a 401 the refresh cannot fix) and lands on the sign-in page.
        current.on('auth.revoked', changed)
        // The first connect of a session is not a reconnect; nothing missed.
        if (bound) changed()
        bound = current
      } else if (current) {
        changed()
      }
    })
    const offRefresh = onSessionRefreshed((info) => void syncAccess(info))
    let lastLook = Date.now()
    const onVisible = () => {
      if (document.visibilityState !== 'visible' || Date.now() - lastLook < 30_000) return
      lastLook = Date.now()
      changed()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      offConnection()
      offRefresh()
      bound?.off('access.changed', changed)
      bound?.off('auth.revoked', changed)
      bound?.off('disconnect', kicked)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [signedIn, syncAccess])

  const patchSession = useCallback((patch: SessionPatch) => {
    const working = sessionRef.current?.property?.id
    setSession((current) =>
      current
        ? {
            ...current,
            user: { ...current.user, ...patch.user },
            property: current.property && { ...current.property, ...patch.property },
          }
        : current,
    )
    // The sign-in page shows the same name and logo, from /branding.
    if (patch.property) refreshBranding()
    // The switcher lists the same property, and would otherwise show the old
    // name the moment it opens.
    if (patch.property && working) {
      setProperties((list) =>
        list.map((property) => (property.id === working ? { ...property, ...patch.property } : property)),
      )
    }
  }, [])

  useEffect(() => {
    if (
      session &&
      isSuperAdmin &&
      !session.property &&
      properties.length === 1 &&
      !resolvingProperty &&
      !autoSelectFailed.current.has(properties[0].id)
    ) {
      // Try each property exactly once. A failure leaves `session.property`
      // null and clears `resolvingProperty`, which is precisely the state this
      // effect fires on — so without the guard a single failing selection
      // (a property deleted out from under the session returns 404) becomes an
      // unbounded retry loop that saturates the API from the browser: twelve
      // thousand requests in two minutes, and a spinner that never resolves.
      const target = properties[0].id
      void selectProperty(target).catch((error: Error) => {
        autoSelectFailed.current.add(target)
        setPropertyError(error.message || 'That property could not be opened.')
      })
    }
  }, [session, isSuperAdmin, properties, resolvingProperty, selectProperty])

  const value = useMemo<AuthContextValue>(() => {
    const held = new Set(session?.permissions ?? [])
    const has = (permission: string) => held.has(permission) || held.has('system:admin')
    return {
      session,
      loading,
      signIn,
      signOut,
      refresh: load,
      can: (...permissions) => permissions.every(has),
      canAny: (...permissions) => permissions.some(has),
      properties,
      propertiesLoaded,
      resolvingProperty,
      propertyError,
      selectProperty,
      patchSession,
    }
  }, [
    session,
    loading,
    signIn,
    signOut,
    load,
    properties,
    propertiesLoaded,
    resolvingProperty,
    propertyError,
    selectProperty,
    patchSession,
  ])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used inside an AuthProvider')
  return context
}

/**
 * Render children only if the permission is held.
 *
 * Note what this does *not* do: it does not grey anything out. A user who
 * cannot do something does not see a teasing disabled button — the control is
 * absent (docs/08-uiux.md §3).
 */
export function PermissionGate({
  permission,
  anyOf,
  fallback = null,
  children,
}: {
  permission?: string
  anyOf?: string[]
  fallback?: ReactNode
  children: ReactNode
}) {
  const { can, canAny } = useAuth()
  const allowed = permission ? can(permission) : anyOf ? canAny(...anyOf) : true
  return <>{allowed ? children : fallback}</>
}
